// PORTIERT nach Shopify. Der ?v=-Cache-Buster von früher ist weg: Liquids
// asset_url hängt selbst einen Versions-Hash an, ein zweites ?v= erzeugte eine
// kaputte URL mit zwei Fragezeichen. Der Import läuft über die Import-Map in
// templates/page.sober.liquid ('@sober/scene' -> sober-scene.js mit Hash),
// nicht relativ — so ist die Datei immer sauber versioniert adressiert.
import { initProductScene } from '@sober/scene';

// Erste ausgeführte Modul-Zeile: belegt, dass die ES-Modul-Kette (main -> scene ->
// three vom CDN) wirklich lief. Das klassische Inline-Fallback in index.html prüft
// dieses Flag — fehlt es nach dem Timeout (CDN blockiert, offline, alter Webview ohne
// Import-Maps), schaltet es die Seite in den statischen Fallback (Ladescreen weg,
// Inhalte sichtbar, Poster an), statt blank hinter dem Ladescreen hängen zu bleiben.
document.documentElement.classList.add('enhanced');

// Ingredients overlay panel
const dialog = document.getElementById('ingredients-panel');
const openBtn = document.getElementById('ingredients-toggle');
const closeBtn = document.getElementById('ingredients-close');

openBtn?.addEventListener('click', () => dialog?.showModal());
closeBtn?.addEventListener('click', () => dialog?.close());
dialog?.addEventListener('click', (e) => {
  if (e.target === dialog) dialog.close();
});

// ---- Unified scroll-driven UI: reveals, sticky CTA, progress bar ----
// One rAF-throttled scroll handler drives all three. Reveals are geometry-based
// (getBoundingClientRect) rather than IntersectionObserver on purpose — per CLAUDE.md
// the in-house preview tool silently never fires IO callbacks, and the sticky CTA was
// already moved to this same proven scroll-listener pattern for that reason.
const hero = document.getElementById('hero');
const stickyCta = document.getElementById('sticky-cta');
const progressBar = document.getElementById('scroll-progress');
const section01El = document.getElementById('section-01');
const section04El = document.getElementById('section-04');
const dockMq = window.matchMedia('(min-width: 760px)');

// ---- Manifesto: scroll-driven word-by-word highlight (Referenz: usevault.ai) ----
// The paragraph is split into word spans only when JS runs AND the user allows motion;
// otherwise it stays a plain, fully visible paragraph (the dimming CSS only targets
// the spans). A visually-hidden copy keeps the sentence readable as one unit for
// screen readers instead of word-by-word fragments.
const manifestoText = document.getElementById('manifesto-text');
const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
let manifestoWords = [];
if (manifestoText && !prefersReducedMotion) {
  const full = manifestoText.textContent.trim();
  manifestoText.textContent = '';
  const sr = document.createElement('span');
  sr.className = 'sr-only';
  sr.textContent = full;
  manifestoText.appendChild(sr);
  full.split(/\s+/).forEach((word) => {
    const span = document.createElement('span');
    span.className = 'manifesto-word';
    span.setAttribute('aria-hidden', 'true');
    span.textContent = word;
    manifestoText.appendChild(span);
    manifestoText.appendChild(document.createTextNode(' '));
  });
  manifestoWords = Array.from(manifestoText.querySelectorAll('.manifesto-word'));
}

// Live list of elements still waiting to reveal; each is spliced out once shown.
const pendingReveals = Array.from(document.querySelectorAll('[data-reveal]'));

// Pre-assign a staggered delay per group so siblings crossing the trigger together
// (e.g. the whole hero on load) cascade instead of snapping in as one block.
document.querySelectorAll('[data-reveal-group]').forEach((group) => {
  group.querySelectorAll('[data-reveal]').forEach((el, i) => {
    el.style.setProperty('--reveal-delay', `${i * 70}ms`);
  });
});

// Count-up for [data-count] stat values, started when their reveal fires (Referenz:
// Cape Town Dam Levels). Markup ships the final value, so no-JS shows it as-is; the
// animation resets to 0 only at the moment the element becomes visible.
function startCounters(revealedEl) {
  const counters = revealedEl.matches('[data-count]')
    ? [revealedEl]
    : Array.from(revealedEl.querySelectorAll('[data-count]'));
  counters.forEach((el) => {
    const target = parseInt(el.dataset.count, 10);
    if (!Number.isFinite(target)) return;
    const unit = el.dataset.unit || '';
    if (prefersReducedMotion) {
      el.textContent = target + unit;
      return;
    }
    const t0 = performance.now();
    const dur = 1400;
    function tick(t) {
      const p = Math.min((t - t0) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      el.textContent = Math.round(eased * target) + unit;
      if (p < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  });
}

// ---- Sci-Fi-Konnektor: Callout-Linie vom aktiven Text zur angedockten Dose ----
// (Spyros Wunsch: der Text "verbindet sich" mit der Dose; beim Weiterscrollen löst
// sich die Linie und der nächste Abschnitt dockt neu an.) Die Text-x-Kante ist
// scroll-invariant und wird pro Sektion glyph-genau via Range gecacht (volle Boxen
// wie h2 melden sonst Spaltenbreite); die y-Koordinate folgt dem Scroll.
const connectorEl = document.getElementById('connector');
const connectorLine = document.getElementById('connector-line');
const connectorDot = document.getElementById('connector-dot');
const dockEl = document.getElementById('can-dock');
// Sektion 04 gehört nicht dazu: dort ist die Dose bereits nach oben rausgefahren
// (Daten-Sektion bleibt frei), eine Linie hätte kein Ziel mehr.
const connectorSections = [
  section01El,
  document.getElementById('section-02'),
  document.getElementById('section-03'),
].filter(Boolean);
const inkRightCache = new Map();
let connectorActive = null;

window.addEventListener('resize', () => inkRightCache.clear(), { passive: true });
// Der gecachte Glyph-Rand der h2 wird ggf. noch mit der Fallback-Schrift gemessen,
// falls Space Grotesk (font-display: swap) beim ersten Docken noch nicht dekodiert ist.
// Nach dem Font-Swap einmal invalidieren -> nächster Konnektor-Pass misst neu.
if (document.fonts && document.fonts.ready) {
  document.fonts.ready.then(() => { inkRightCache.clear(); onScroll(); });
}

// Fester Ankerpunkt am Text (Spyro: "Punkte fest machen, nur die Linie mitbewegen"):
// der Punkt sitzt rechts neben der Sektions-Überschrift und scrollt MIT ihr — nicht
// mehr am Viewport entlanggleitend. x (Glyph-Kante der h2, scroll-invariant) wird
// gecacht; y kommt pro Frame live vom Layout.
function sectionAnchor(sec) {
  const h2 = sec.querySelector('h2');
  if (!h2) return null;
  let inkRight = inkRightCache.get(sec);
  if (inkRight === undefined) {
    const range = document.createRange();
    range.selectNodeContents(h2);
    inkRight = range.getBoundingClientRect().right;
    inkRightCache.set(sec, inkRight);
  }
  const hr = h2.getBoundingClientRect();
  return { x: inkRight + 14, y: hr.top + hr.height / 2 };
}

function updateConnector(docked, vh) {
  if (!connectorEl || !connectorLine || !connectorDot || !dockEl) return;

  let active = null;
  if (docked) {
    // Aktiv ist die Sektion, deren Mitte 55% Viewporthöhe am nächsten kommt.
    let bestDist = Infinity;
    connectorSections.forEach((sec) => {
      const r = sec.getBoundingClientRect();
      if (r.bottom < vh * 0.18 || r.top > vh * 0.85) return;
      const d = Math.abs(r.top + r.height / 2 - vh * 0.55);
      if (d < bestDist) { bestDist = d; active = sec; }
    });
  }
  if (!active) {
    connectorEl.classList.remove('is-active');
    connectorActive = null;
    return;
  }

  const anchor = sectionAnchor(active);
  if (!anchor) {
    connectorEl.classList.remove('is-active');
    connectorActive = null;
    return;
  }
  const dr = dockEl.getBoundingClientRect();
  const x2 = dr.left - 6;
  const y2 = dr.top + dr.height / 2;
  const x1 = Math.min(anchor.x, x2 - 40);
  const y1 = anchor.y;
  const mx = Math.max(x1 + 24, x2 - 56);
  connectorLine.setAttribute('points', `${x1},${y1} ${mx},${y1} ${x2},${y2}`);
  connectorDot.setAttribute('cx', x1);
  connectorDot.setAttribute('cy', y1);

  const len = Math.max(mx - x1, 0) + Math.hypot(x2 - mx, y2 - y1);
  connectorLine.style.strokeDasharray = `${len}`;
  if (active !== connectorActive) {
    connectorActive = active;
    // Draw-in von der Textseite zur Dose: Transition kurz abschalten, Offset auf
    // volle Länge setzen, Reflow erzwingen, dann animiert auf 0 ziehen — so
    // startet die Linie auch bei schnellen Sektionswechseln sauber neu.
    connectorLine.style.transition = 'none';
    connectorLine.style.strokeDashoffset = `${len}`;
    connectorLine.getBoundingClientRect();
    connectorLine.style.transition = 'stroke-dashoffset 0.45s ease';
  }
  connectorLine.style.strokeDashoffset = '0';
  connectorEl.classList.add('is-active');
}

let ticking = false;
let firstPass = true;
function updateScrollUI() {
  ticking = false;
  const vh = window.innerHeight;
  // Some in-app webviews report a 0-height viewport on the very first pass (before the
  // layout viewport is sized). Bail so above-fold content isn't judged against a
  // trigger of 0 and left hidden — a later pass (load / resize / scroll) handles it.
  if (!vh) return;

  // First valid pass reveals everything already on screen (top < vh) so nothing in the
  // opening viewport — e.g. a headline line sitting just below the fold — stays hidden
  // waiting for a scroll. Subsequent (scroll-driven) passes use the tighter 86% line so
  // elements animate in with a little anticipation as they enter.
  if (pendingReveals.length) {
    const trigger = firstPass ? vh : vh * 0.86;
    for (let i = pendingReveals.length - 1; i >= 0; i--) {
      const el = pendingReveals[i];
      if (el.getBoundingClientRect().top < trigger) {
        el.classList.add('is-revealed');
        startCounters(el);
        pendingReveals.splice(i, 1);
      }
    }
    firstPass = false;
  }

  // Manifesto highlight — words light up (and dim again when scrolling back) mapped
  // onto the paragraph's travel from 85% viewport height up to 35%.
  if (manifestoWords.length) {
    const rect = manifestoText.getBoundingClientRect();
    const start = vh * 0.85;
    const travel = rect.height + vh * 0.5;
    const p = Math.min(Math.max((start - rect.top) / travel, 0), 1);
    const lit = Math.round(p * manifestoWords.length);
    manifestoWords.forEach((w, i) => w.classList.toggle('lit', i < lit));
  }

  // Sticky CTA — appears once the hero has scrolled fully out of view.
  if (hero && stickyCta) {
    stickyCta.classList.toggle('visible', hero.getBoundingClientRect().bottom <= 0);
  }

  // Dock-Zustand (nur Desktop): sobald Sektion 01 heranscrollt, weichen die
  // Sektionsnummern, der Text rückt nach links und die Dock-Silhouette rechts
  // blendet ein (die 3D-Pose folgt ihr in js/scene.js). Kurz vor dem CTA-Block
  // wird wieder gelöst, damit die fixe Outline nicht über dem CTA hängt.
  if (section01El && section04El) {
    // Dock-Fenster: von Sektion 01 bis zum ENDE von Sektion 03 (= Oberkante von 04
    // erreicht die Viewport-Mitte) — die Verbindung soll bis zum Ende von "Kauen.
    // Feiern. Wiederholen." bestehen bleiben (Spyro), erst dann fährt die Dose raus.
    const docked = dockMq.matches &&
      section01El.getBoundingClientRect().top < vh * 0.6 &&
      section04El.getBoundingClientRect().top > vh * 0.4;
    // Der 42ch-Cap im Dock-Zustand ändert den Textumbruch — die gecachten
    // Glyph-Kanten des Konnektors gelten dann nicht mehr.
    if (docked !== document.body.classList.contains('is-docked')) inkRightCache.clear();
    document.body.classList.toggle('is-docked', docked);
    updateConnector(docked, vh);
  }

  // Reading-progress bar.
  if (progressBar) {
    const doc = document.documentElement;
    const max = doc.scrollHeight - vh;
    const p = max > 0 ? Math.min(Math.max(doc.scrollTop / max, 0), 1) : 0;
    progressBar.style.transform = `scaleX(${p})`;
  }
}
function onScroll() {
  if (!ticking) {
    ticking = true;
    requestAnimationFrame(updateScrollUI);
  }
}
window.addEventListener('scroll', onScroll, { passive: true });
window.addEventListener('resize', onScroll, { passive: true });
// Run the first pass synchronously, then again once fonts/layout have fully settled —
// covers webviews that only report a real viewport height after the load event.
updateScrollUI();
window.addEventListener('load', updateScrollUI);
setTimeout(updateScrollUI, 250);

// ---- Countdown to the next weekend (Referenz: cage.berlin) ----
// Ticks toward Friday 22:00 local time; inside the "party window" (Fr 22:00 – So 08:00)
// it flips to a pulsing JETZT instead. The ticking span is aria-hidden — the label text
// alone reads as a complete statement for screen readers.
const cdLabel = document.getElementById('countdown-label');
const cdTimer = document.getElementById('countdown-timer');
const cdDays = document.getElementById('cd-days');
const cdHours = document.getElementById('cd-hours');
const cdMins = document.getElementById('cd-mins');
const cdSecs = document.getElementById('cd-secs');

function updateCountdown() {
  const now = new Date();
  const day = now.getDay();
  const hour = now.getHours();
  const isLive = (day === 5 && hour >= 22) || day === 6 || (day === 0 && hour < 8);

  if (isLive) {
    if (cdLabel) cdLabel.textContent = 'Die Nacht läuft.';
    cdTimer.classList.add('is-live');
    return;
  }
  cdTimer.classList.remove('is-live');
  if (cdLabel) cdLabel.textContent = 'Nächster Rave in';

  const target = new Date(now);
  target.setDate(now.getDate() + ((5 - day + 7) % 7));
  target.setHours(22, 0, 0, 0);
  if (target <= now) target.setDate(target.getDate() + 7);

  let s = Math.floor((target - now) / 1000);
  const d = Math.floor(s / 86400); s -= d * 86400;
  const h = Math.floor(s / 3600); s -= h * 3600;
  const m = Math.floor(s / 60); s -= m * 60;
  const pad = (n) => String(n).padStart(2, '0');
  // Jede Einheit in ihre eigene Ziffern-Kachel schreiben (Tage/Std/Min/Sek).
  if (cdDays) cdDays.textContent = pad(d);
  if (cdHours) cdHours.textContent = pad(h);
  if (cdMins) cdMins.textContent = pad(m);
  if (cdSecs) cdSecs.textContent = pad(s);
}

if (cdTimer) {
  updateCountdown();
  setInterval(updateCountdown, 1000);
}

// 3D hero centerpiece — onFrame füttert das Live-Rotations-Readout UND legt das HUD
// pro Frame um die aktuelle Bildschirmposition der Dose (es reist die Journey mit).
// DOM-Writes für das Readout nur, wenn sich der gerundete Gradwert tatsächlich ändert.
const hudEl = document.getElementById('hud');
const hudRotation = document.getElementById('hud-rotation');
const heroSectionEl = document.getElementById('hero');
let hudLastDeg = -1;
let hudVisible = false;

initProductScene({
  canvas: document.getElementById('product-canvas'),
  posterEl: document.getElementById('product-poster'),
  loaderEl: document.getElementById('loader'),
  motionGate: document.getElementById('motion-gate'),
  motionEnableBtn: document.getElementById('motion-enable'),
  motionSkipBtn: document.getElementById('motion-skip'),
  // PORTIERT: Der Pfad kann hier nicht mehr hart stehen. Erstens ist assets/ in
  // Shopify flach (kein models/-Unterordner), zweitens braucht die URL den
  // Versions-Hash aus asset_url — und eine .js-Datei in assets/ wertet kein
  // Liquid aus. Die Template-Seite schreibt die fertige URL darum als
  // data-model-url an #hero-stage; wir lesen sie hier aus dem DOM.
  // Ausfallverhalten beachten: bei falscher/fehlender URL zeigt scene.js still
  // die Platzhalter-Kapsel statt einer Fehlermeldung (siehe CLAUDE.md).
  modelUrl: document.getElementById('hero-stage')?.dataset.modelUrl,
  // Intro-Ladescreen: Fortschritt speist den Balken, onReady blendet ihn aus. Das
  // window.eniqaLoader-Objekt kommt aus dem klassischen Inline-Skript in index.html
  // (läuft auch, falls dieses Modul gar nicht lädt) — Optional-Chaining, falls nicht da.
  onProgress: (p) => window.eniqaLoader?.setProgress(p),
  onReady: () => window.eniqaLoader?.done(),
  // Scroll-Journey: Anker-Rechtecke für die Pose (Hero -> Viewport-Mitte -> Dock ->
  // Exit vor Sektion 04) und die Drag-Flächen für den pointer-events:none-Canvas.
  anchors: {
    heroStage: document.getElementById('hero-stage'),
    section01: section01El,
    section04: section04El,
    dock: document.getElementById('can-dock'),
  },
  dragSurfaces: [
    document.getElementById('hero-stage'),
    document.getElementById('can-dock'),
  ],
  onFrame: (rotY, rect) => {
    // Live-Rotations-Readout
    if (hudRotation) {
      const deg = Math.round((((rotY * 180 / Math.PI) % 360) + 360) % 360) % 360;
      if (deg !== hudLastDeg) {
        hudLastDeg = deg;
        hudRotation.textContent = `ROT ${String(deg).padStart(3, '0')}°`;
      }
    }
    // HUD folgt der Dose: die vier Eck-Klammern um ihre aktuelle Bildschirm-Box legen.
    if (hudEl && rect && rect.size > 0 && Number.isFinite(rect.cx)) {
      if (hudEl.hidden) hudEl.hidden = false; // erst zeigen, wenn die Szene wirklich rendert
      // Box bewusst BREITER als die Dose, damit die Eck-Readouts seitlich Abstand zur
      // schmalen Dosen-Silhouette halten (Spyro: „Overlay ist zu nah an der Dose" — es
      // klebte v.a. horizontal, KOFFEIN 152 mg lag über dem Dosenkörper). Höhe bleibt
      // knapp (die Dose ist zwischen den beiden h1-Zeilen „Bleib wach."/„Feier durch."
      // eingeklemmt, nur ~30px Luft je Seite — mehr Höhe würde die Readouts in die
      // Headline schieben).
      const w = rect.size * 1.55;
      const h = rect.size * 1.08;
      const left = rect.cx - w / 2;
      const right = rect.cx + w / 2;
      const top = rect.cy - h / 2;
      hudEl.style.width = `${Math.round(w)}px`;
      hudEl.style.height = `${Math.round(h)}px`;
      hudEl.style.transform = `translate(${Math.round(left)}px, ${Math.round(top)}px)`;
      // HUD NUR zeigen, wenn wirklich Platz ist (Spyro) — sonst lieber ganz aus, statt zu
      // überlappen oder am Rand abgeschnitten zu werden:
      //  - komplett im Viewport (beide Seiten, kein Clipping am rechten Rand),
      //  - Dose nicht angedockt (dort steht der Sektionstext daneben -> Überlappung),
      //  - groß genug und vertikal noch im Bild.
      // Deckt kleine Screens automatisch mit ab: passt die Box nicht, bleibt das HUD weg.
      const vh = window.innerHeight;
      const vw = window.innerWidth;
      const docked = document.body.classList.contains('is-docked');
      const fitsH = left >= 6 && right <= vw - 6;
      const onScreenV = rect.cy + h / 2 > 48 && top < vh - 8;
      // HUD ist ein reines Hero-Element: sobald man aus dem Hero rausscrollt, tritt es
      // KOMPLETT in den Hintergrund (Spyro) — es darf nicht mit der schwebenden Dose über
      // Marquee/Manifest/Sektionstext ziehen und den Text überlagern. Solange die
      // Unterkante der Hero-Section noch unter 60 % Viewport-Höhe liegt, sind wir im Hero;
      // danach blendet das HUD über die CSS-Opacity-Transition sauber aus.
      const inHero = heroSectionEl ? heroSectionEl.getBoundingClientRect().bottom > vh * 0.6 : true;
      const show = inHero && onScreenV && fitsH && !docked && rect.size > 24;
      if (show !== hudVisible) {
        hudVisible = show;
        hudEl.classList.toggle('is-visible', show);
      }
    }
  },
});
